import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Spinner,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  Input, Select, Field,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  makeStyles, tokens,
} from '@fluentui/react-components'
import { ArrowDownload24Regular, Delete24Regular } from '@fluentui/react-icons'
import { useAuth } from '../../auth/useAuth'
import { activityLogService } from '../../services/activityLogService'
import { NativeDateInput } from '../../components/NativeDateInput'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'

const PAGE_SIZE = 50

const ACTIONS = [
  'Vytvoření', 'Úprava', 'Smazání', 'Deaktivace', 'Schválení',
  'Zamítnutí', 'Stornování', 'Import', 'Konfigurace', 'Přiřazení',
  'Splnění', 'Vrácení splnění', 'Korekce dovolené', 'Notifikace',
]

const ENTITY_TYPES = [
  'Zaměstnanec', 'Externista', 'Žádost o nepřítomnost',
  'Dokument (šablona)', 'Dokument', 'Příspěvek na stáří',
  'Zůstatek dovolené', 'Nastavení', 'Import', 'E-mail',
]

const useStyles = makeStyles({
  header: {
    display: 'flex', alignItems: 'center', gap: '12px',
    marginBottom: '20px',
  },
  headerActions: {
    marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center',
  },
  filters: {
    display: 'flex', gap: '10px', marginBottom: '12px',
    flexWrap: 'wrap', alignItems: 'flex-end',
  },
  filterField: { minWidth: '150px' },
  th: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3, textTransform: 'uppercase',
  },
  tableRow: {
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  pagination: {
    display: 'flex', alignItems: 'center', gap: '12px',
    marginTop: '12px', justifyContent: 'space-between',
  },
  paginationRight: {
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  noData: {
    padding: '40px', textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  roleBadge: {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
})

function formatTs(ts: string) {
  // EF Core vrací datetime2 bez timezone indikátoru — přidáme 'Z' pro správné UTC čtení
  const utc = ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z'
  return new Date(utc).toLocaleString('cs-CZ', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function ActivityLogPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()

  // Filters (applied on Search)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [emailFilter, setEmailFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [entityFilter, setEntityFilter] = useState('')

  // Applied filters (sent to API)
  const [applied, setApplied] = useState({
    from: '', to: '', userEmail: '', action: '', entityType: '',
  })

  const [page, setPage] = useState(1)
  const [exportLoading, setExportLoading] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['activity-log', page, applied],
    queryFn: () => activityLogService.getAll({
      page,
      pageSize: PAGE_SIZE,
      from:       applied.from       || undefined,
      to:         applied.to         || undefined,
      userEmail:  applied.userEmail  || undefined,
      action:     applied.action     || undefined,
      entityType: applied.entityType || undefined,
    }),
    enabled: isAdmin,
  })

  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } =
    useAutoRetry(isError, refetch)

  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  const handleSearch = () => {
    setApplied({ from: fromDate, to: toDate, userEmail: emailFilter, action: actionFilter, entityType: entityFilter })
    setPage(1)
  }

  const handleReset = () => {
    setFromDate(''); setToDate(''); setEmailFilter(''); setActionFilter(''); setEntityFilter('')
    setApplied({ from: '', to: '', userEmail: '', action: '', entityType: '' })
    setPage(1)
  }

  const handleExport = async () => {
    setExportLoading(true)
    try {
      await activityLogService.exportExcel({
        from:       applied.from       || undefined,
        to:         applied.to         || undefined,
        userEmail:  applied.userEmail  || undefined,
        action:     applied.action     || undefined,
        entityType: applied.entityType || undefined,
      })
    } finally {
      setExportLoading(false)
    }
  }

  const handleDeleteAll = async () => {
    setDeleteLoading(true)
    try {
      await activityLogService.deleteAll()
      setDeleteDialogOpen(false)
      setPage(1)
      queryClient.invalidateQueries({ queryKey: ['activity-log'] })
    } finally {
      setDeleteLoading(false)
    }
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Text size={500} weight="semibold" style={{ color: tokens.colorPaletteRedForeground1 }}>
          Přístup odepřen. Tato stránka je dostupná pouze uživatelům s rolí Admin.
        </Text>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className={styles.header}>
        <Text size={600} weight="semibold">Log aktivit</Text>
        {isLoading && <Spinner size="tiny" />}
        <div className={styles.headerActions}>
          <Button
            icon={exportLoading ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
            disabled={exportLoading}
            onClick={handleExport}
          >
            {exportLoading ? 'Připravuji…' : 'Export (Excel)'}
          </Button>
          <Button
            appearance="subtle"
            icon={<Delete24Regular />}
            style={{ color: tokens.colorPaletteRedForeground3 }}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Vymazat log
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <Field label="Datum od" className={styles.filterField}>
          <NativeDateInput value={fromDate} onChange={setFromDate} />
        </Field>
        <Field label="Datum do" className={styles.filterField}>
          <NativeDateInput value={toDate} onChange={setToDate} min={fromDate || undefined} />
        </Field>
        <Field label="E-mail uživatele" className={styles.filterField} style={{ minWidth: '200px' }}>
          <Input
            value={emailFilter}
            onChange={(_, d) => setEmailFilter(d.value)}
            placeholder="např. jana@firma.cz"
            onKeyDown={e => { if (e.key === 'Enter') handleSearch() }}
          />
        </Field>
        <Field label="Akce" className={styles.filterField}>
          <Select value={actionFilter} onChange={(_, d) => setActionFilter(d.value)}>
            <option value="">Vše</option>
            {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>
        </Field>
        <Field label="Entita" className={styles.filterField}>
          <Select value={entityFilter} onChange={(_, d) => setEntityFilter(d.value)}>
            <option value="">Vše</option>
            {ENTITY_TYPES.map(e => <option key={e} value={e}>{e}</option>)}
          </Select>
        </Field>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', paddingBottom: '1px' }}>
          <Button appearance="primary" onClick={handleSearch}>Vyhledat</Button>
          <Button appearance="subtle" onClick={handleReset}>Resetovat</Button>
        </div>
      </div>

      {/* Error bar */}
      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst log aktivit."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      {/* Table */}
      <Table size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell className={styles.th} style={{ width: '145px' }}>Datum/čas</TableHeaderCell>
            <TableHeaderCell className={styles.th}>Uživatel</TableHeaderCell>
            <TableHeaderCell className={styles.th} style={{ width: '80px' }}>Role</TableHeaderCell>
            <TableHeaderCell className={styles.th} style={{ width: '120px' }}>Akce</TableHeaderCell>
            <TableHeaderCell className={styles.th} style={{ width: '160px' }}>Entita</TableHeaderCell>
            <TableHeaderCell className={styles.th} style={{ width: '60px' }}>ID</TableHeaderCell>
            <TableHeaderCell className={styles.th}>Popis</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={7}>
                <div style={{ padding: '32px', textAlign: 'center' }}>
                  <Spinner size="medium" />
                </div>
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !isError && data?.items.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <div className={styles.noData}>Žádné záznamy.</div>
              </TableCell>
            </TableRow>
          )}
          {data?.items.map(log => (
            <TableRow key={log.id} className={styles.tableRow}>
              <TableCell>
                <span style={{ fontSize: '13px', color: tokens.colorNeutralForeground2, whiteSpace: 'nowrap' }}>
                  {formatTs(log.timestamp)}
                </span>
              </TableCell>
              <TableCell>
                <span style={{ fontSize: '13px' }}>{log.userEmail}</span>
              </TableCell>
              <TableCell>
                <span className={styles.roleBadge}>{log.userRole}</span>
              </TableCell>
              <TableCell>
                <span style={{ fontSize: '13px' }}>{log.action}</span>
              </TableCell>
              <TableCell>
                <span style={{ fontSize: '13px' }}>{log.entityType}</span>
              </TableCell>
              <TableCell>
                <span style={{ fontSize: '13px', color: tokens.colorNeutralForeground3 }}>
                  {log.entityId ?? '—'}
                </span>
              </TableCell>
              <TableCell>
                <span style={{ fontSize: '13px' }}>{log.description}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      {!isLoading && !isError && (
        <div className={styles.pagination}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Celkem: {totalCount} záznamů
          </Text>
          <div className={styles.paginationRight}>
            <Button appearance="subtle" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              ← Předchozí
            </Button>
            <Text size={300}>Strana {page} / {totalPages}</Text>
            <Button appearance="subtle" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Další →
            </Button>
          </div>
        </div>
      )}

      {/* Dialog: Vymazat celý log */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, s) => { if (!deleteLoading) setDeleteDialogOpen(s.open) }}>
        <DialogSurface>
          <DialogTitle>Vymazat celý log aktivit?</DialogTitle>
          <DialogBody>
            <DialogContent>
              Tato akce je nevratná. Budou trvale odstraněny všechny záznamy
              ({totalCount > 0 ? `${totalCount} záznamů` : 'všechny záznamy'}).
            </DialogContent>
            <DialogActions>
              <Button
                appearance="subtle"
                disabled={deleteLoading}
                onClick={() => setDeleteDialogOpen(false)}
              >
                Zrušit
              </Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                icon={deleteLoading ? <Spinner size="tiny" /> : undefined}
                disabled={deleteLoading}
                onClick={handleDeleteAll}
              >
                {deleteLoading ? 'Mažu…' : 'Vymazat log'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
